import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Button, Card, CardBody } from "@material-tailwind/react";
import PsychBlockedDatesPicker from "../components/admin/PsychBlockedDatesPicker";
import PsychologistPhotoSpecialtyBlock from "../components/admin/PsychologistPhotoSpecialtyBlock";
import { usePortalAuth } from "../context/PortalAuthContext";
import { portalApi } from "../services/portalApi";
import { PortalShell } from "../components/PortalShell";
import WeeklyScheduleEditor from "../components/admin/WeeklyScheduleEditor";
import ConsultationModePicker from "../components/admin/ConsultationModePicker";
import MexicoLocationSelects, {
  locationToPayload,
} from "../components/admin/MexicoLocationSelects";
import {
  defaultWeeklyTemplate,
  normalizeScheduleClient,
} from "../utils/psychSchedule";
import { todayMxYmd } from "../utils/dateMx";
import { CM_BOTH, CM_IN_PERSON, CM_VIRTUAL } from "../utils/consultationModes";

export default function PortalAdminPsychSchedule() {
  const { psychologistId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = usePortalAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [psych, setPsych] = useState(null);
  const [weekly, setWeekly] = useState(defaultWeeklyTemplate());
  const [blockedDates, setBlockedDates] = useState([]);
  const [lastModified, setLastModified] = useState(null);
  const [address, setAddress] = useState("");
  const [locationSel, setLocationSel] = useState({
    stateCode: "",
    municipalityCode: "",
  });
  const [consultationModes, setConsultationModes] = useState(CM_BOTH);
  const [specialtyDescription, setSpecialtyDescription] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== "admin") return;
    let cancelled = false;

    async function loadPsych() {
      let p = null;
      try {
        const r = await portalApi.adminPsychologist(psychologistId);
        p = r.psychologist;
      } catch (err) {
        // Compat: Lambda antigua sin GET por id
        if (err?.status === 404) {
          const list = await portalApi.adminPsychologists({ includeSchedule: true });
          p = (list.psychologists || []).find((x) => x.psychologistId === psychologistId) || null;
        } else {
          throw err;
        }
      }
      if (cancelled) return;
      setPsych(p || null);
      const n = normalizeScheduleClient(p?.schedule);
      setBlockedDates(n.blockedDates || []);
      setAddress((p?.address && String(p.address)) || "");
      const last = n.versions[n.versions.length - 1];
      if (last?.weekly?.length) setWeekly(last.weekly);
      setLastModified(last?.effectiveFrom || null);
      if (p?.location?.stateCode && p?.location?.municipalityCode) {
        setLocationSel({
          stateCode: p.location.stateCode,
          municipalityCode: p.location.municipalityCode,
        });
      } else {
        setLocationSel({ stateCode: "", municipalityCode: "" });
      }
      const cm = p?.consultationModes;
      setConsultationModes(
        cm === CM_IN_PERSON || cm === CM_VIRTUAL || cm === CM_BOTH ? cm : CM_BOTH
      );
      setSpecialtyDescription(
        (p?.specialtyDescription && String(p.specialtyDescription)) || ""
      );
    }

    loadPsych()
      .catch(() => toast.error("No se pudo cargar el psicólogo"))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user, psychologistId]);

  async function handleAdminPhotoChange(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !psych) return;
    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error("La imagen debe pesar como máximo 5 MB");
      return;
    }
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    const ct = allowed.includes(file.type) ? file.type : "image/jpeg";
    if (file.type && !allowed.includes(file.type)) {
      toast.error("Usa una imagen JPG, PNG o WebP");
      return;
    }
    setUploadingPhoto(true);
    try {
      const r = await portalApi.adminPsychologistPhotoUploadUrl({
        psychologistId: psych.psychologistId,
        contentType: ct,
      });
      const putRes = await fetch(r.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": r.contentType || ct },
      });
      if (!putRes.ok) throw new Error("upload");
      await portalApi.adminUpdatePsychologist({
        psychologistId: psych.psychologistId,
        profileImageKey: r.imageKey,
      });
      const detail = await portalApi.adminPsychologist(psychologistId);
      if (detail?.psychologist) setPsych(detail.psychologist);
      toast.success("Foto actualizada");
    } catch (err) {
      const msg =
        err?.status === 503
          ? "Las fotos no están disponibles (falta configurar el bucket en el servidor)."
          : err.message || "No se pudo subir la foto";
      toast.error(msg);
    } finally {
      setUploadingPhoto(false);
    }
  }

  async function saveAll() {
    if (!psych) return;
    const loc = locationToPayload(locationSel.stateCode, locationSel.municipalityCode);
    if (!loc) {
      toast.error("Selecciona estado y municipio o alcaldía de la consulta presencial.");
      return;
    }
    setSaving(true);
    try {
      await portalApi.adminUpdatePsychologist({
        psychologistId: psych.psychologistId,
        consultationModes,
        specialtyDescription: specialtyDescription.trim() || null,
        location: loc,
        address: address.trim(),
        schedule: {
          blockedDates,
          versions: [{ effectiveFrom: todayMxYmd(), weekly }],
        },
      });
      toast.success("Horario actualizado");
      navigate("/admin/psicologos");
    } catch (e) {
      toast.error(e.message || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role !== "admin") {
    if (user?.role === "psychologist") {
      return <Navigate to="/psicologo" replace />;
    }
    return <Navigate to="/panel" replace />;
  }

  if (!loading && !psych) {
    return (
      <PortalShell title="Psicólogo no encontrado" subtitle="">
        <Link to="/admin/psicologos" className="text-brand-lavender font-medium">
          Volver a administración
        </Link>
      </PortalShell>
    );
  }

  return (
    <PortalShell wide>
      {loading && <p className="text-brand-gray text-sm">Cargando…</p>}

      {!loading && psych && (
        <div className="mx-auto max-w-6xl pb-24">
          {/* Back link + page title — aligned with the cards */}
          <div className="mb-6">
            <Link
              to="/admin/psicologos"
              className="portal-meta font-medium text-brand-lavender hover:underline"
            >
              ← Volver a administración
            </Link>
            <h1 className="mt-2 font-sans text-2xl sm:text-3xl font-semibold tracking-tight text-brand-dark">
              {psych.name}
            </h1>
            <p className="mt-1 text-brand-gray text-sm sm:text-base max-w-xl leading-relaxed">
              Perfil, ubicación, modalidad y horario.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            {/* Columna izquierda: perfil */}
            <div className="space-y-5">
              <Card className="border border-brand-brown/15">
                <CardBody className="p-5 sm:p-6 space-y-3">
                  <h2 className="portal-section-title">Foto y especialidad</h2>
                  <PsychologistPhotoSpecialtyBlock
                    idPrefix="admin-sched"
                    profileImageUrl={psych.profileImageUrl}
                    localPreviewUrl={null}
                    specialtyDescription={specialtyDescription}
                    onSpecialtyChange={setSpecialtyDescription}
                    onFileChange={handleAdminPhotoChange}
                    uploadingPhoto={uploadingPhoto}
                    disabled={saving}
                  />
                </CardBody>
              </Card>

              <Card className="border border-brand-brown/15">
                <CardBody className="p-5 sm:p-6 space-y-3">
                  <h2 className="portal-section-title">Modalidad de consulta</h2>
                  <p className="portal-meta">
                    Define si los pacientes pueden elegir presencial, virtual o ambas al reservar.
                  </p>
                  <ConsultationModePicker
                    value={consultationModes}
                    onChange={setConsultationModes}
                    disabled={saving}
                  />
                </CardBody>
              </Card>

              <Card className="border border-brand-brown/15">
                <CardBody className="p-5 sm:p-6 space-y-3">
                  <h2 className="portal-section-title">Ubicación presencial (México)</h2>
                  <p className="portal-meta">Catálogo INEGI (estado + municipio o alcaldía).</p>
                  <MexicoLocationSelects value={locationSel} onChange={setLocationSel} />
                  <div className="space-y-1.5 mt-1">
                    <label className="portal-label">Dirección</label>
                    <textarea
                      rows={3}
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Calle, número, colonia, referencias…"
                      className="w-full rounded-xl border border-blue-gray-200 bg-white px-3.5 py-2.5 text-[0.9375rem] text-brand-dark placeholder:text-brand-gray/60 focus:border-brand-lavender focus:outline-none focus:ring-2 focus:ring-brand-lavender/20 disabled:opacity-60 resize-none transition-colors"
                    />
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Columna derecha: agenda */}
            <div className="space-y-5">
              <Card className="border border-brand-brown/15">
                <CardBody className="p-5 sm:p-6 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="portal-section-title">Horario</h2>
                    {lastModified && (
                      <span className="portal-meta">
                        Último cambio: <span className="font-medium text-brand-dark">{lastModified}</span>
                      </span>
                    )}
                  </div>

                  <WeeklyScheduleEditor weekly={weekly} onChange={setWeekly} />
                </CardBody>
              </Card>

              <Card className="border border-brand-brown/15">
                <CardBody className="p-5 sm:p-6 space-y-3">
                  <h2 className="portal-section-title">Días no disponibles</h2>
                  <PsychBlockedDatesPicker
                    blockedYmd={blockedDates}
                    onChange={setBlockedDates}
                  />
                </CardBody>
              </Card>
            </div>
          </div>

          {/* Barra de acciones fija */}
          <div className="fixed inset-x-0 bottom-0 z-40 border-t border-brand-lavender/20 bg-white/95 backdrop-blur-md shadow-[0_-2px_12px_rgba(65,68,109,0.08)]">
            <div className="mx-auto flex max-w-6xl items-center justify-end gap-3 px-4 py-3">
              <Button
                type="button"
                variant="text"
                className="text-brand-dark normal-case"
                onClick={() => navigate("/admin/psicologos")}
              >
                Cancelar
              </Button>
              <Button
                className="bg-brand-lavender normal-case"
                disabled={saving || uploadingPhoto}
                onClick={saveAll}
              >
                {saving ? "Guardando…" : "Guardar cambios"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PortalShell>
  );
}
